/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@mui/material';
import { SelectionMode } from '@grapecity/wijmo.grid';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { getExcelFileName } from 'utils/ExcelUtil';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { PopupSearchParam, UtMatrixHookupTag } from 'models/ut/UtMatrixHookupTag';
import { getUtMatrixHookupTagList } from 'apis/ut/UtMatrixHookupTag';
import CustomDialog from 'components/modals/common/CustomDialog';
import { CommonUtil } from 'utils/CommonUtil';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';

/**
 * @desc UT Matrix Hookup Tag List 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.03.25   jihwan89       E20260318-001141 기계 리스트에 사용량 추가
 * 2026.08.05   jihwan89       CSR E20260727-000228 Vacuum Pump 추가
 */

interface Props {
  open: boolean;
  close: () => void;
  condition: any;
}

const UtMatrixHookupTagPopup = ({ open, close, condition, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();

  const [flexRef, setFlexRef] = useState<any>();

  const [rowData, setRowData] = useState<UtMatrixHookupTag[]>([]);

  const [searchParam, setSearchParam] = useState<PopupSearchParam>({
    utmId: condition?.utmId || '',
    planProcId: condition?.planProcId || '',
    utmSeq: condition?.utmSeq || '',
    tagDivsCd: condition?.tagDivsCd || '',
  });

  useEffect(() => {
    btnSearch(searchParam);
  }, []);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        header: String(t('ut.label.Hookup Tagging', 'Hookup Tagging')),
        columns: [
          {
            binding: 'tagDivsCdNm',
            header: String(t('ut.label.기계/전기', '기계/전기')),
            width: 120,
            align: 'center',
          },
          {
            binding: 'tagId',
            header: String(t('ut.label.TAG ID', 'TAG ID')),
            width: 250,
          },
        ],
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('ut.label.공정', '공정')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'eqclIdNm',
        header: String(t('ut.label.설비군', '설비군')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'dtalProcCdNm',
        header: String(t('ut.label.세부공정', '세부공정')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'eqpMainNm',
        header: String(t('ut.label.Main', 'Main')),
        width: 120,
      },
      {
        binding: 'eqpMchNm',
        header: String(t('ut.label.Machine', 'Machine')),
        width: 150,
      },
      {
        binding: 'stndEqpId',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        width: 180,
        align: 'center',
      },
      {
        binding: 'bizAreaNm',
        header: String(t('ut.label.Area', 'Area')),
        width: 80,
        align: 'center',
      },
      {
        binding: 'bldgFloCdNm',
        header: String(t('ut.label.Floor', 'Floor')),
        width: 80,
        align: 'center',
      },
      {
        binding: 'prdnLnNm',
        header: String(t('ut.label.Line', 'Line')),
        width: 80,
        align: 'center',
      },
      {
        binding: 'prdnPrlLnNm',
        header: String(t('ut.label.Sub Line', 'Sub Line')),
        width: 90,
        align: 'center',
      },
      {
        header: String(t('ut.label.수량', '수량')),
        columns: [
          {
            binding: 'machEqpQty',
            header: String(t('ut.label.기계', '기계')),
            width: 80,
            align: 'center',
          },
          {
            binding: 'elpwEqpQty',
            header: String(t('ut.label.전기', '전기')),
            width: 80,
            align: 'center',
          },
        ],
      },
      {
        binding: 'vltgNvl',
        header: String(t('ut.label.Voltage', 'Voltage')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'elpwPhasCd',
        header: String(t('ut.label.Phase', 'Phase')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'ctwTpCd',
        header: String(t('ut.label.Wire', 'Wire')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'od1EcNvl',
        header: String(t('ut.label.Current(입력값)', 'Current(입력값)')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'brkeCapa',
        header: String(t('ut.label.Breaker(MCCB)', 'Breaker(MCCB)')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'elpwCapa',
        header: String(t('ut.label.Electricity Capacity', 'Electricity Capacity')),
        width: 150,
        align: 'center',
      },
      {
        binding: 'cdaDevcCnctTpCd',
        header: String(t('ut.label.Connection[Type]', 'Connection[Type]')),
        width: 140,
        align: 'center',
      },
      {
        header: String(t('ut.label.CDA', 'CDA')),
        columns: [
          {
            binding: 'cdaPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'cdaCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.N2', 'N2')),
        columns: [
          {
            binding: 'nitrPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'nitrCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.IW', 'IW')),
        columns: [
          {
            binding: 'iwPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'iwCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.FW', 'FW')),
        columns: [
          {
            binding: 'frwtPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'frwtCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.PCW', 'PCW')),
        columns: [
          {
            binding: 'coolPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'coolCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.WW', 'WW')),
        columns: [
          {
            binding: 'wwPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'wwCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Steam', 'Steam')),
        columns: [
          {
            binding: 'stemPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'stemCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.NG', 'NG')),
        columns: [
          {
            binding: 'lngPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'lngCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Return Air', 'Return Air')),
        columns: [
          {
            binding: 'insdExhaPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'insdExhaCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Exhaust Air', 'Exhaust Air')),
        columns: [
          {
            binding: 'otsdExhaPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'otsdExhaCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      // 2026.08.05 CSR E20260727-000228 Vacuum Pump 추가
      {
        header: String(t('ut.label.Vacuum Pump', 'Vacuum Pump')),
        columns: [
          {
            binding: 'vacmPumpPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'vacmPumpCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Supply Air', 'Supply Air')),
        columns: [
          {
            binding: 'suarPntCnt',
            header: String(t('ut.label.Qty', 'Qty')),
            width: 80,
            align: 'right',
            cellTemplate: (params) =>
              `${params.value ? Number(params.value).toLocaleString() : ''}`,
          },
          {
            binding: 'suarCspSumCapa',
            header: String(t('ut.label.사용량', '사용량')),
            width: 80,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      // {
      //   binding: 'cdaPntCnt',
      //   header: String(t('ut.label.CDA(Qty)', 'CDA(Qty)')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'nitrPntCtn',
      //   header: String(t('ut.label.N2(Qty)', 'N2(Qty)')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'iwPntCnt',
      //   header: String(t('ut.label.IW(Qty)', 'IW(Qty)')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'frwtPntCnt',
      //   header: String(t('ut.label.FW(Qty)', 'FW(Qty)')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'coolPntCnt',
      //   header: String(t('ut.label.PCW(Qty)', 'PCW(Qty)')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'wwPntCnt',
      //   header: String(t('ut.label.WW(Qty)', 'WW(Qty)')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'stemPntCnt',
      //   header: String(t('ut.label.Steam(Qty)', 'Steam(Qty)')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'lngPntCnt',
      //   header: String(t('ut.label.NG(Qty)', 'NG(Qty)')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'insdExhaPntCnt',
      //   header: String(t('ut.label.Retrun Air(Qty)', 'Retrun Air(Qty)')),
      //   width: 140,
      //   align: 'center',
      // },
      // {
      //   binding: 'otsdExhaPntCnt',
      //   header: String(t('ut.label.Exhaust Air(Qty)', 'Exhaust Air(Qty)')),
      //   width: 140,
      //   align: 'center',
      // },
      // {
      //   binding: 'suarPntCnt',
      //   header: String(t('ut.label.Supply Air(Qty)', 'Supply air(Qty)')),
      //   width: 140,
      //   align: 'center',
      // },
      // {
      //   binding: 'dataInsUserNm',
      //   header: String(t('ut.label.등록자', '등록자')),
      //   width: 100,
      //   align: 'center',
      // },
      // {
      //   binding: 'dataInsDtm',
      //   header: String(t('ut.label.등록일', '등록일')),
      //   width: 100,
      //   align: 'center',
      // },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    setFlexRef(grid);
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });

      grid.invalidate();
    });
  };

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'Hookup tagging list';
    book.saveAsync(getExcelFileName(t('ut.label.Hookup Tagging List', 'Hookup Tagging List')));
  };

  const btnSearch = (searchParam) => {
    getUtMatrixHookupTagList(searchParam).then((result: UtMatrixHookupTag[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  return (
    <CustomDialog
      title={t('ut.label.Hookup Tagging List', 'Hookup Tagging List')}
      open={open}
      onClose={close}
      size={'xl'}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.Hookup Tagging List', 'Hookup Tagging List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => btnSearch(searchParam)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        height={400}
        excludeFilter={[]}
        excludePin={[]}
        autoCheck={true}
        isReadOnly={true}
        selectionMode={SelectionMode.Row}
      />
    </CustomDialog>
  );
};

export default UtMatrixHookupTagPopup;
